/**
 * Notifications Service — TS Orchestration API
 *
 * Wraps the notification endpoints:
 *   GET    /api/notifications
 *   GET    /api/notifications/unread-count
 *   POST   /api/notifications/:id/mark-read
 *   POST   /api/notifications/mark-all-read
 *   DELETE /api/notifications/:id
 *   POST   /api/notifications
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, type QueryKey } from '@tanstack/react-query';
import { queryClient } from '../queryClient';
import { supabase } from '../supabase';

const getApi = () => import('../api').then((m) => m.api);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// CANONICAL FIELD NAMES (UAT PR-2 schema fence): this shape mirrors exactly
// what GET /api/notifications now returns — the new-schema notification
// columns, unmapped. Unread status is read_at === null (no separate boolean
// flag); do not add one back. The schema fence (scripts/check-schema-fence.sh)
// blocks new source references to this type's old, now-retired field names
// — the API no longer exposes them, so there is nothing to read here even if
// you wanted to.
export interface AppNotification {
  id: string;
  notification_type: string;
  title: string;
  body?: string;
  link?: string;
  read_at: string | null;
  payload?: Record<string, unknown>;
  created_at: string;
}

export interface CreateNotificationPayload {
  recipient_id: string;
  type: string;
  title: string;
  content?: string;
  action_url?: string;
  metadata?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// ALLOWED_NOTIFICATION_TYPES — FE-only display allow-list (UAT PR-2, 2026-08-10)
//
// The backend emits notification types the bell was never designed to show
// in bulk: on staging alone there are 474 unread `calendar_event_updated`
// rows (routine backlog noise) and 27 `tutor_intervention_alert` rows (a
// tutor feature that is dark in prod — see YUDU_AI_FEATURES_ENABLED). Without
// filtering, mounting the real bell (this PR, part a) would flood every
// tenant user with those instead of the grading-workflow notifications they
// actually need to act on.
//
// This is a FE-only, reversible allow-list: the backend keeps writing every
// type unchanged, nothing is deleted, and widening the list later is a
// one-line change here. It is applied consistently to the popover list, the
// full NotificationCenter page, and the unread badge count (see
// fetchAllowedUnreadCount below) so the badge never promises more than the
// list shows.
// ---------------------------------------------------------------------------
export const ALLOWED_NOTIFICATION_TYPES = new Set<string>([
  'period_for_advisor',
  'period_for_registrar',
  'period_locked',
  'period_returned',
  'student_enrolled',
  'course_submitted',
]);

export const isAllowedNotification = (n: { notification_type: string }): boolean =>
  ALLOWED_NOTIFICATION_TYPES.has(n.notification_type);

// ---------------------------------------------------------------------------
// RECENT_FETCH_WINDOW — UAT 8/10 round-2 fix: badge/popover consistency
//
// loadItems() used to fetch only the newest 10 notifications of ALL types
// (read+unread) before filtering — a window shallow enough that the
// allow-list could starve it completely: if the 10 most recent rows are all
// `calendar_event_updated` noise, the popover shows "No notifications yet"
// even though several allow-listed rows exist just beyond that window and
// the badge (which scans the newest 100 UNREAD rows — see
// fetchAllowedUnreadCount) reports a nonzero count. Over-fetch this much
// deeper pool, filter, THEN slice to the caller's display limit.
// ---------------------------------------------------------------------------
const RECENT_FETCH_WINDOW = 50;

// ---------------------------------------------------------------------------
// fetchNotifications — paginated list for current user
// ---------------------------------------------------------------------------

export const fetchNotifications = async (params?: {
  unread_only?: boolean;
  limit?: number;
  offset?: number;
}): Promise<{ notifications: AppNotification[]; total: number }> => {
  const api = await getApi();
  const q = new URLSearchParams();
  if (params?.unread_only) q.set('unread_only', 'true');
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.offset) q.set('offset', String(params.offset));
  const res = await api.get(`/api/notifications?${q.toString()}`);
  return res.data;
};

// ---------------------------------------------------------------------------
// fetchUnreadCount — quick badge count
// ---------------------------------------------------------------------------

export const fetchUnreadCount = async (): Promise<number> => {
  const api = await getApi();
  const res = await api.get('/api/notifications/unread-count');
  return res.data?.unread_count ?? 0;
};

// ---------------------------------------------------------------------------
// fetchAllowedUnreadCount — badge count, recomputed client-side against the
// allow-list (UAT PR-2)
//
// /api/notifications/unread-count is a SERVER count of every unread row
// regardless of type, so it can't be trusted for the badge once the FE
// starts hiding types (an excluded-only unread backlog would show a nonzero
// badge over an empty-looking bell). Instead, fetch the most recent unread
// rows and count only the ones the allow-list would actually render, so the
// badge always matches what a user sees after opening the bell.
//
// Pagination caveat: this fetches at most `limit` unread rows (newest
// first). If a user has more than `limit` unread notifications and the
// allowed-type ones are older than that many excluded-type rows (e.g. a
// long run of calendar_event_updated pushes a period_for_advisor row past
// the window), the count can under-count until the newer noise is read or
// cleared. This is a deliberate FE-only tradeoff — an exact count would
// need a server-side type filter on /unread-count, which is out of scope
// for this reversible, FE-only change.
// ---------------------------------------------------------------------------
export const fetchAllowedUnreadCount = async (limit = 100): Promise<number> => {
  const { notifications } = await fetchNotifications({ unread_only: true, limit });
  return notifications.filter(isAllowedNotification).length;
};

// ---------------------------------------------------------------------------
// markRead — mark single notification as read
// ---------------------------------------------------------------------------

export const markRead = async (id: string): Promise<void> => {
  const api = await getApi();
  await api.post(`/api/notifications/${id}/mark-read`);
};

// ---------------------------------------------------------------------------
// markAllRead — mark all as read
// ---------------------------------------------------------------------------

export const markAllRead = async (): Promise<void> => {
  const api = await getApi();
  await api.post('/api/notifications/mark-all-read');
};

// ---------------------------------------------------------------------------
// deleteNotification — remove single notification
// ---------------------------------------------------------------------------

export const deleteNotification = async (id: string): Promise<void> => {
  const api = await getApi();
  await api.delete(`/api/notifications/${id}`);
};

// ---------------------------------------------------------------------------
// createNotification — used by other services to emit notifications
// ---------------------------------------------------------------------------

export const createNotification = async (payload: CreateNotificationPayload): Promise<void> => {
  const api = await getApi();
  await api.post('/api/notifications', payload);
};

// ---------------------------------------------------------------------------
// useNotifications — hook for the bell icon + dropdown
//
// Fetches unread count on mount, re-fetches on Supabase realtime INSERT,
// and exposes list/mark-read operations for the dropdown.
// ---------------------------------------------------------------------------

export const NOTIFICATIONS_POLL_MS = 60_000;

export const unreadNotificationsQueryKey = (userId?: string | null): QueryKey => ['notifications', 'unreadCount', userId ?? null];

export const useNotifications = (userId?: string) => {
  const countKey = unreadNotificationsQueryKey(userId);
  const countQuery = useQuery(
    {
      queryKey: countKey,
      queryFn: () => fetchAllowedUnreadCount(),
      enabled: userId != null,
      staleTime: 0,
      retry: false,
      refetchInterval: NOTIFICATIONS_POLL_MS,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
    },
    queryClient,
  );

  const unreadCount = countQuery.data ?? 0;
  const setUnreadCount = useCallback(
    (next: number | ((current: number) => number)) =>
      queryClient.setQueryData<number>(unreadNotificationsQueryKey(userId), (current) =>
        typeof next === 'function' ? next(current ?? 0) : next,
      ),
    [userId],
  );
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const { refetch: refetchCount } = countQuery;
  const refreshCount = useCallback(async () => {
    await refetchCount();
  }, [refetchCount]);

  // loadItems — popover list fetch (UAT 8/10 round-2: badge/popover
  // consistency). Over-fetches RECENT_FETCH_WINDOW (50) notifications of ALL
  // types, filters via the allow-list, THEN slices to `limit` for display —
  // the popover still shows at most `limit` items, but draws them from a
  // pool deep enough that the common case (well under RECENT_FETCH_WINDOW
  // total recent notifications) can't be starved by interleaved excluded-type
  // noise the way a shallow limit:10 fetch could.
  //
  // unreadCount is ALSO re-derived here from that same fetched + filtered
  // pool (unsliced), so the instant the popover opens, the badge/header count
  // and the visible list are guaranteed to come from one consistent snapshot
  // — they can no longer disagree the way "badge 14 / popover 5" could
  // before. Between opens, the independent 15s poll (refreshCount →
  // fetchAllowedUnreadCount, unread-only, newest 100) remains the freshness
  // source and keeps its own documented >100-unread caveat unchanged; this
  // only guarantees on-open consistency, which is the concrete symptom this
  // fixes.
  const loadItems = useCallback(async (limit = 10) => {
    setLoadingItems(true);
    try {
      const { notifications } = await fetchNotifications({ limit: RECENT_FETCH_WINDOW });
      const allowed = notifications.filter(isAllowedNotification);
      setItems(allowed.slice(0, limit));
      setUnreadCount(allowed.filter((n) => n.read_at == null).length);
    } catch {
      setItems([]);
    } finally {
      setLoadingItems(false);
    }
  }, [setUnreadCount]);

  const onMarkRead = useCallback(async (id: string) => {
    await markRead(id);
    void queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: now } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
  }, [setUnreadCount]);

  const onMarkAllRead = useCallback(async () => {
    await markAllRead();
    void queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at ?? now })));
    setUnreadCount(0);
  }, [setUnreadCount]);

  const onDelete = useCallback(async (id: string) => {
    const wasUnread = items.find((n) => n.id === id)?.read_at == null;
    await deleteNotification(id);
    void queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
    setItems((prev) => prev.filter((n) => n.id !== id));
    if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1));
  }, [items, setUnreadCount]);

  // Supabase realtime — listen for new notifications for this user
  useEffect(() => {
    if (!userId) return;

    const ch = supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${userId}`,
        },
        (payload) => {
          const n = payload.new as AppNotification;
          // Gate the realtime push through the same allow-list as the
          // initial load/count — otherwise an excluded-type INSERT (e.g. a
          // calendar update) would bump the badge and prepend a row the
          // popover is supposed to be hiding.
          if (!isAllowedNotification(n)) return;
          setUnreadCount((c) => c + 1);
          setItems((prev) => [n, ...prev]);
        },
      )
      .subscribe();

    channelRef.current = ch;
    return () => {
      supabase.removeChannel(ch);
    };
  }, [userId, setUnreadCount]);

  return {
    unreadCount,
    items,
    loadingItems,
    refreshCount,
    loadItems,
    onMarkRead,
    onMarkAllRead,
    onDelete,
  };
};
